import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/joy';
import {
  FacebookLogo as FacebookIcon,
  InstagramLogo as InstagramIcon,
  Buildings as BusinessIcon,
  Check as CheckIcon,
  X as CloseIcon,
  LinkSimple as LinkIcon,
  LinkBreak as LinkOffIcon,
  ArrowsClockwise as RefreshIcon,
  ShieldCheck as SecurityIcon,
  Warning as WarningIcon
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { format } from 'date-fns';

interface SocialMediaAccount {
  id: number;
  platform: string;
  platform_display: string;
  account_name: string;
  account_id: string;
  is_active: boolean;
  connected_at: string;
  last_refreshed: string;
}

interface SocialAccountConnectorProps {
  open: boolean;
  onClose: () => void;
}

export const SocialAccountConnector: React.FC<SocialAccountConnectorProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing accounts
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: async () => {
      const response = await api.getAllPages('/api/marketing/social-accounts/');
      return response.data;
    }
  });

  // Disconnect account mutation
  const disconnectMutation = useMutation({
    mutationFn: async (accountId: number) => {
      return api.delete(`/api/marketing/social-accounts/${accountId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
    }
  });

  // Refresh token mutation
  const refreshTokenMutation = useMutation({
    mutationFn: async (accountId: number) => {
      return api.post(`/api/marketing/social-accounts/${accountId}/refresh_token/`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
    }
  });

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      description: 'Connect your Facebook Page to publish posts and updates',
      icon: <FacebookIcon />,
      color: '#1877F2',
      features: ['Post to Page', 'Schedule posts', 'View insights', 'Manage comments']
    },
    {
      id: 'instagram',
      name: 'Instagram',
      description: 'Connect your Instagram Business account',
      icon: <InstagramIcon />,
      color: '#E4405F',
      features: ['Share posts', 'Schedule content', 'View analytics', 'Manage comments']
    },
    {
      id: 'google_business_profile',
      name: 'Google Business Profile',
      description: 'Connect your Google Business Profile to manage posts and updates',
      icon: <BusinessIcon />,
      color: '#4285F4',
      features: ['Create posts', 'Share updates', 'Upload photos', 'Manage reviews']
    }
  ];

  const handleConnect = async (platform: string) => {
    setIsConnecting(true);
    setError(null);
    setSelectedPlatform(platform);

    try {
      let authUrl = '';
      
      if (platform === 'facebook' || platform === 'instagram') {
        // Get OAuth URL from backend to ensure proper App ID is used
        const redirectUri = `${window.location.origin}/marketing/oauth/${platform}/callback`;
        const state = Math.random().toString(36).substring(7);
        
        try {
          // Build query parameters manually (the API client doesn't support params option)
          const params = new URLSearchParams();
          params.append('platform', platform);
          params.append('redirect_uri', redirectUri);
          params.append('state', state);
          
          const requestUrl = `/api/marketing/social-accounts/oauth-url/?${params.toString()}`;
          console.log('OAuth URL request params:', { platform, redirect_uri: redirectUri, state });
          console.log('Making request to:', requestUrl);
          
          const response = await api.get<{ oauth_url: string }>(requestUrl);
          
          console.log('OAuth URL response:', response.data);
          authUrl = response.data.oauth_url;
        } catch (error: any) {
          console.error('Error getting OAuth URL:', error);
          setError(error.response?.data?.error || 'Failed to get OAuth URL');
          setIsConnecting(false);
          setSelectedPlatform(null);
          return;
        }
      } else if (platform === 'google_business_profile') {
        // Use the dedicated GBP OAuth flow
        try {
          const response = await api.get<{ oauth_url: string }>('/marketing/gbp/connect/');
          if (response && response.data && response.data.oauth_url) {
            window.location.href = response.data.oauth_url;
          }
        } catch (error) {
          console.error('Failed to initiate GBP OAuth:', error);
          setError('Failed to connect to Google Business Profile. Please try again.');
        }
        return;
      } else {
        setError(`Unsupported platform: ${platform}`);
        setIsConnecting(false);
        setSelectedPlatform(null);
        return;
      }

      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        'oauth_popup',
        `width=${width},height=${height},left=${left},top=${top},status=yes,scrollbars=yes,resizable=yes`
      );

      // Check if popup was blocked
      if (!popup || popup.closed) {
        throw new Error('Please allow popups for this site to connect your account');
      }

      // Poll for popup closure
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          setIsConnecting(false);
          setSelectedPlatform(null);
          // Refresh accounts list
          queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
        }
      }, 1000);

    } catch (err: any) {
      setError(err.message || 'Failed to connect account');
      setIsConnecting(false);
      setSelectedPlatform(null);
    }
  };

  const handleDisconnect = async (account: SocialMediaAccount) => {
    if (window.confirm(`Are you sure you want to disconnect ${account.account_name}?`)) {
      await disconnectMutation.mutateAsync(account.id);
    }
  };

  const handleRefreshToken = async (account: SocialMediaAccount) => {
    await refreshTokenMutation.mutateAsync(account.id);
  };

  const getConnectedAccount = (platformId: string) => {
    return accounts?.find((acc: SocialMediaAccount) => acc.platform === platformId);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 800 }}>
        <ModalClose />
        <DialogTitle>Connect Social Media Accounts</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Security Notice */}
            <Alert
              startDecorator={<SecurityIcon />}
              variant="soft"
            >
              Your account credentials are securely handled through official OAuth providers. 
              We never store your passwords.
            </Alert>

            {/* Platform List */}
            {platforms.map((platform) => {
              const connectedAccount = getConnectedAccount(platform.id);
              const isConnected = !!connectedAccount;
              
              return (
                <Card 
                  key={platform.id} 
                  variant={isConnected ? 'soft' : 'outlined'}
                  sx={{ 
                    borderColor: isConnected ? 'success.main' : 'neutral.outlinedBorder',
                    bgcolor: isConnected ? 'success.softBg' : 'background.surface'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Platform Icon */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 'md',
                          bgcolor: platform.color,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {platform.icon}
                      </Box>

                      {/* Platform Info */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography level="title-lg">{platform.name}</Typography>
                          {isConnected && (
                            <Chip
                              size="sm"
                              color="success"
                              startDecorator={<CheckIcon />}
                            >
                              Connected
                            </Chip>
                          )}
                        </Box>
                        
                        <Typography level="body-sm" sx={{ mb: 2 }}>
                          {platform.description}
                        </Typography>

                        {/* Features */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {platform.features.map((feature) => (
                            <Chip key={feature} size="sm" variant="outlined">
                              {feature}
                            </Chip>
                          ))}
                        </Box>

                        {/* Connected Account Info */}
                        {isConnected && connectedAccount && (
                          <Card variant="outlined" size="sm" sx={{ mb: 2 }}>
                            <CardContent>
                              <Typography level="body-sm" fontWeight="lg">
                                {connectedAccount.account_name}
                              </Typography>
                              <Typography level="body-xs">
                                Connected {format(new Date(connectedAccount.connected_at), 'MMM d, yyyy')}
                              </Typography>
                              {connectedAccount.last_refreshed && (
                                <Typography level="body-xs">
                                  Last refreshed {format(new Date(connectedAccount.last_refreshed), 'MMM d, yyyy h:mm a')}
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {isConnected ? (
                            <>
                              <Button
                                size="sm"
                                variant="outlined"
                                color="danger"
                                startDecorator={<LinkOffIcon />}
                                onClick={() => handleDisconnect(connectedAccount!)}
                                loading={disconnectMutation.isPending}
                              >
                                Disconnect
                              </Button>
                              <Button
                                size="sm"
                                variant="outlined"
                                color="primary"
                                startDecorator={<RefreshIcon />}
                                onClick={() => handleRefreshToken(connectedAccount!)}
                                loading={refreshTokenMutation.isPending}
                              >
                                Refresh Token
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="solid"
                              color="primary"
                              startDecorator={
                                isConnecting && selectedPlatform === platform.id ? (
                                  <CircularProgress size="sm" />
                                ) : (
                                  <LinkIcon />
                                )
                              }
                              onClick={() => handleConnect(platform.id)}
                              disabled={isConnecting}
                            >
                              {isConnecting && selectedPlatform === platform.id
                                ? 'Connecting...'
                                : 'Connect Account'}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Error Display */}
            {error && (
              <Alert
                color="danger"
                startDecorator={<WarningIcon />}
                endDecorator={
                  <IconButton size="sm" variant="plain" onClick={() => setError(null)}>
                    <CloseIcon />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}

            {/* Help Section */}
            <Card variant="soft" color="neutral">
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  Need help connecting your accounts?
                </Typography>
                <Typography level="body-sm">
                  Make sure you have admin access to the social media accounts you want to connect. 
                  For business accounts, ensure they are properly configured with the required permissions.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};